import QRCode from 'qrcode';
import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import type {
  ExterneFreimeldung,
  FreimeldungLink,
  FreimeldungQrQuelle,
  Report,
} from '../types';
import {
  freimeldungLinkUrl,
  istFreimeldungAktiv,
  offeneItemIdsFuerBericht,
  type MangelServerStatus,
} from './freimeldungLogic';

/**
 * Der ANGEMELDETE Teil der externen Freimeldung (docs/freimeldung-online-plan.md
 * §D, §F) — Tabellen `report_freimeldung_links` und
 * `inspection_item_freimeldungen`, Migration Nr. 108/109.
 *
 * Die REGELN liegen daneben in `freimeldungLogic.ts` und sind ohne Datenbank
 * pruefbar; hier steht nur, was ohne Supabase nicht zu haben ist.
 *
 * ═══ FAIL-OPEN, UND ZWAR ABSICHTLICH ═══════════════════════════════════════
 *
 * Der QR-Code ist eine BEIGABE zum Bericht. Ein Versand darf nie daran
 * scheitern, dass die Migration fehlt, RLS zuschlaegt (Freelancer: 42501) oder
 * PostgREST die Funktion nicht kennt (PGRST202). Deshalb geben
 * `bereiteFreimeldungLinkVor` und `ladeFreimeldungQrFuerBericht` bei JEDEM
 * Fehler `null` zurueck und schreiben eine Warnung — gedruckt wird dann eben
 * kein Kasten, kein Platzhalter (Muster `ladeEinweisungQrFuerProjekt`).
 *
 * Die Gegenrichtung gilt fuer `entscheideFreimeldung`: eine Entscheidung des
 * Koordinators DARF nicht still verschwinden, sie wirft.
 *
 * ═══ DER MANGEL WIRD HIER NICHT GESCHLOSSEN ════════════════════════════════
 *
 * `report_freimeldung_entscheiden` ruehrt `inspection_items` nicht an. Der
 * Mangel wird im Client ueber `statusWechselAnwenden` geschlossen und ganz
 * normal gespeichert; erst DANACH wird die Meldung als bestaetigt gebucht.
 * Andersherum stuende die Meldung auf `bestaetigt` und der Mangel weiter
 * offen, wenn der Save scheitert.
 *
 * Ob er geglueckt ist, beantwortet NICHT das Speichersignal der Oberflaeche,
 * sondern `mangelServerStatus` — eine Lesefrage an genau diesen Mangel.
 */

const T_LINKS = 'report_freimeldung_links';
const T_MELDUNGEN = 'inspection_item_freimeldungen';
const T_ITEMS = 'inspection_items';

/** Der Eimer, in dem die Beweisfotos liegen (`api/freimeldung-foto.ts`). */
const FOTO_EIMER = 'project-assets';

type Zeile = Record<string, unknown>;

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const strOrNull = (v: unknown): string | null => {
  if (v == null) return null;
  const s = str(v).trim();
  return s ? s : null;
};
const numOrNull = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Erster gesetzter Wert unter mehreren Schluesseln — die Quelle darf snake ODER camel liefern. */
const pick = (row: Zeile, ...keys: string[]): unknown => {
  for (const k of keys) if (row?.[k] !== undefined && row?.[k] !== null) return row[k];
  return undefined;
};

const errorCode = (e: unknown): string => {
  const c = (e as { code?: unknown } | null)?.code;
  return typeof c === 'string' ? c : '';
};

/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205; die RPC fehlt als PGRST202. */
const istFehlendesSchema = (e: unknown): boolean => {
  const code = errorCode(e);
  return code === 'PGRST205' || code === '42P01' || code === 'PGRST202' || code === '42883';
};

/** jsonb-Antwort einer RPC als Objekt; Zeichenketten werden geparst, alles andere ist null. */
const alsObjekt = (data: unknown): Zeile | null => {
  if (data == null) return null;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === 'object' ? (parsed as Zeile) : null;
    } catch {
      return null;
    }
  }
  return typeof data === 'object' ? (data as Zeile) : null;
};

// ─── Mapper ─────────────────────────────────────────────────────────────────

const STATUS = new Set(['offen', 'bestaetigt', 'abgelehnt']);

/**
 * Aus dem Objektpfad im Eimer wird die oeffentliche URL — IM CLIENT, nicht in
 * der Datenbank.
 *
 * Die Spalte heisst seit dem Umbau `foto_pfad` und traegt nur noch den Pfad
 * (`<pid>/freimeldungen/<token12>/<uuid>.jpg`). Die Datenbank schneidet ihn
 * beim Melden aus der URL heraus, die `/api/freimeldung-foto` zurueckgibt;
 * hier wird er wieder zusammengesetzt. Damit haengt die gespeicherte Zeile
 * nicht mehr an einem Hostnamen, den ein Melder mitgeschickt hat.
 *
 * ZWEI PFADE WERDEN VERWORFEN, beide werden zu `null` (kein Bild ist besser
 * als ein falsches):
 *   • alles, was mit `http` beginnt — das ist keine Angabe im Eimer, sondern
 *     eine fertige Fremd-URL und zeigte womoeglich irgendwohin;
 *   • alles mit `..` — ein Pfad, der aus dem Eimer herausklettert.
 */
const oeffentlicheFotoUrl = (pfad: string | null): string | null => {
  const p = (pfad || '').trim();
  if (!p) return null;
  if (p.toLowerCase().startsWith('http') || p.includes('..')) return null;
  try {
    const url = supabase.storage?.from(FOTO_EIMER)?.getPublicUrl(p)?.data?.publicUrl;
    return strOrNull(url);
  } catch (e) {
    console.warn('Beweisfoto-URL konnte nicht gebildet werden — die Meldung bleibt ohne Bild.', e);
    return null;
  }
};

/** Tabellenzeile → Meldung. Ein unbekannter Status gilt als `offen`, damit eine
 *  Zeile aus einer neueren Datenbank nicht stumm aus allen Listen faellt. */
export const zeileZuFreimeldung = (row: Zeile): ExterneFreimeldung => {
  const status = str(pick(row, 'status'));
  const fotoPfad = strOrNull(pick(row, 'fotoPfad', 'foto_pfad'));
  return {
    id: str(pick(row, 'id')),
    linkId: str(pick(row, 'linkId', 'link_id')),
    projectId: str(pick(row, 'projectId', 'project_id')),
    itemId: str(pick(row, 'itemId', 'item_id')),
    ticketNumber: numOrNull(pick(row, 'ticketNumber', 'ticket_number')),
    itemTitle: strOrNull(pick(row, 'itemTitle', 'item_title')),
    firma: str(pick(row, 'firma')),
    name: str(pick(row, 'name')),
    email: strOrNull(pick(row, 'email')),
    phone: strOrNull(pick(row, 'phone')),
    kommentar: strOrNull(pick(row, 'kommentar')),
    // Ohne Spalte (vor Nr. 184) oder mit unbekanntem Wert: eine Freimeldung —
    // die Zurueckweisung ist die Ausnahme, die ausdruecklich dastehen muss.
    art: str(pick(row, 'art')) === 'zurueckgewiesen' ? 'zurueckgewiesen' : 'behoben',
    fotoPfad,
    // NUR aus dem Pfad — es gibt keinen Rueckfall mehr auf eine gespeicherte
    // `foto_url`. Migration Nr. 108 loescht die Spalte; ein Rueckfall darauf
    // hiesse, dass eine Zeile, die sie doch noch traegt (alter Stand, fremde
    // Kopie, wiederhergestelltes Backup), eine ungepruefte Fremd-URL an den
    // Bildschirm des Koordinators braechte — an genau der Pruefung vorbei, die
    // `oeffentlicheFotoUrl` fuehrt. Ohne Pfad bleibt das Bild weg.
    fotoUrl: oeffentlicheFotoUrl(fotoPfad),
    gemeldetAt: str(pick(row, 'gemeldetAt', 'gemeldet_at')),
    status: (STATUS.has(status) ? status : 'offen') as ExterneFreimeldung['status'],
    bearbeitetVon: strOrNull(pick(row, 'bearbeitetVon', 'bearbeitet_von')),
    bearbeitetAt: strOrNull(pick(row, 'bearbeitetAt', 'bearbeitet_at')),
    bearbeitungsKommentar: strOrNull(pick(row, 'bearbeitungsKommentar', 'bearbeitungs_kommentar')),
  };
};

/** Tabellenzeile → Link. `itemIds` bleibt notfalls leer, nie `undefined`. */
export const zeileZuLink = (row: Zeile): FreimeldungLink => ({
  id: str(pick(row, 'id')),
  projectId: str(pick(row, 'projectId', 'project_id')),
  inspectionNumber: numOrNull(pick(row, 'inspectionNumber', 'inspection_number')) ?? 0,
  sectionId: strOrNull(pick(row, 'sectionId', 'section_id')),
  reportDate: strOrNull(pick(row, 'reportDate', 'report_date')),
  itemIds: (() => {
    const v = pick(row, 'itemIds', 'item_ids');
    return Array.isArray(v) ? v.map((x) => str(x)).filter(Boolean) : [];
  })(),
  token: str(pick(row, 'token')),
  aktivBis: strOrNull(pick(row, 'aktivBis', 'aktiv_bis')),
  createdAt: str(pick(row, 'createdAt', 'created_at')),
});

/** Abgelaufen heisst abgelaufen — `aktiv_bis` leer bedeutet unbefristet. */
const linkAbgelaufen = (aktivBis: unknown, jetzt: Date = new Date()): boolean => {
  const bis = strOrNull(aktivBis);
  if (!bis) return false;
  const ms = Date.parse(bis);
  return Number.isNaN(ms) ? false : ms <= jetzt.getTime();
};

/** Nur ein reines Tagesdatum darf als `report_date` in die Datenbank. */
const alsTagesdatum = (v: unknown): string | null => {
  const s = strOrNull(v);
  return s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

// ─── Zeitbudget ─────────────────────────────────────────────────────────────

/**
 * Wie lange der Versand auf den QR-Code hoechstens wartet.
 *
 * `bereiteFreimeldungLinkVor` wird im Sendepfad VOR `generatePDF` abgewartet
 * (EmailModal). Ein haengender Aufruf — PostgREST antwortet nicht, das Netz ist
 * halb da, der Schema-Cache laedt — haette den Versand ohne dieses Budget
 * unbegrenzt aufgehalten: kein Fehler, kein Abbruch, nur ein Knopf, der nicht
 * mehr weitergeht. Fail-open heisst auch zeitlich fail-open.
 *
 * 8 Sekunden: lang genug fuer einen langsamen ersten Aufruf (Schema-Cache,
 * Mobilfunk), kurz genug, dass niemand glaubt, die App sei stehen geblieben.
 */
export const FREIMELDUNG_ZEITBUDGET_MS = 8_000;

/** Der Verlierer des Rennens — ein Symbol, damit es mit keinem Nutzwert kollidiert. */
const ZEIT_ABGELAUFEN = Symbol('freimeldung-zeitbudget');

/**
 * Laesst `arbeit` gegen die Uhr laufen.
 *
 * Der Wecker RESOLVED (er wirft nicht): ein abgewiesenes Promise, das niemand
 * mehr abfaengt, landete sonst als unhandled rejection im Protokoll — dasselbe
 * Muster wie `acquireProjectLockWithin` in `projectLockService.ts`. Der Timer
 * wird IMMER geloescht, auch wenn `arbeit` wirft; sonst hielte er in einem Test
 * mit falschen Uhren die Schleife offen.
 *
 * Die spaete Antwort laeuft im Hintergrund zu Ende — sie interessiert dann
 * niemanden mehr, abbrechen laesst sich ein PostgREST-Aufruf ohnehin nicht.
 */
const mitZeitbudget = async <T>(
  arbeit: Promise<T>,
  ms: number = FREIMELDUNG_ZEITBUDGET_MS,
): Promise<T | typeof ZEIT_ABGELAUFEN> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const wecker = new Promise<typeof ZEIT_ABGELAUFEN>((resolve) => {
    timer = setTimeout(() => resolve(ZEIT_ABGELAUFEN), ms);
  });
  try {
    return await Promise.race([arbeit, wecker]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
};

// ─── Link + QR fuer das PDF ─────────────────────────────────────────────────

/**
 * Legt den Link fuer GENAU DIESEN Bericht an (RPC
 * `report_freimeldung_link_setzen`) und macht den QR-Code daraus.
 *
 * Idempotent: Schluessel ist `(project_id, inspection_number, section_key)`.
 * Ein Wiederholungsversand aktualisiert nur die Mangel-Momentaufnahme und
 * liefert DENSELBEN Token — ein bereits ausgedrucktes oder abfotografiertes
 * PDF bleibt gueltig.
 *
 * `null` bei jedem Fehler (fehlende Migration, 42501 fuer Freelancer, kein
 * Netz) und auch ohne Berichtsnummer: Der Versand laeuft dann ohne Kasten
 * weiter.
 *
 * NICHT FREIGESCHALTET HEISST: GAR NICHT FRAGEN. Ist die Online-Freimeldung
 * fuer dieses Projekt aus (`metadata.freimeldung.aktiv`, Vorgabe an), gibt es
 * hier `null`, OHNE die RPC zu rufen — sonst entstuende beim ersten Versand
 * ein Link, den niemand bestellt hat, und die Datenbank truege Tokens fuer
 * Projekte, die gar nicht mitmachen. Die RPC weist denselben Fall zwar auch
 * ab (22023, Nr. 109), aber das waere ein Fehler im Protokoll statt einer
 * bewussten Entscheidung.
 *
 * ZEITLICH BEGRENZT (`FREIMELDUNG_ZEITBUDGET_MS`): RPC UND QR-Erzeugung
 * zusammen haben 8 Sekunden. Danach `null` — der Bericht geht ohne Kasten
 * raus, statt dass der Versand am QR-Code haengen bleibt.
 */
export const bereiteFreimeldungLinkVor = async (
  projectId: string,
  report: Report,
  origin: string,
  pathname: string,
): Promise<FreimeldungQrQuelle | null> => {
  try {
    if (!istFreimeldungAktiv(report?.metadata)) return null;
    const id = (projectId || '').trim();
    const nummer = numOrNull(report?.metadata?.inspectionNumber);
    if (!id || !nummer) return null;

    const itemIds = offeneItemIdsFuerBericht(report);
    // EIN Budget fuer beide Schritte: zwei eigene Uhren summierten sich im
    // schlimmsten Fall auf das Doppelte, und dem Versender ist egal, welcher
    // der beiden Schritte gerade haengt.
    const ergebnis = await mitZeitbudget(
      (async (): Promise<FreimeldungQrQuelle | null> => {
        const { data, error } = await supabase.rpc('report_freimeldung_link_setzen', {
          p_project_id: id,
          p_inspection_number: nummer,
          p_section_id: strOrNull(report?.metadata?.sectionId),
          p_report_date: alsTagesdatum(report?.metadata?.date),
          p_item_ids: itemIds,
        });
        if (error) throw error;

        const row = alsObjekt(data);
        const token = strOrNull(pick(row ?? {}, 'token'));
        if (!token) return null;

        const url = freimeldungLinkUrl(origin, pathname, token);
        const dataUrl = await QRCode.toDataURL(url, { width: 512, margin: 1 });
        return { token, url, dataUrl, inspectionNumber: nummer };
      })(),
    );
    if (ergebnis === ZEIT_ABGELAUFEN) {
      console.warn(
        `Freimelde-Link nicht innerhalb von ${FREIMELDUNG_ZEITBUDGET_MS} ms erhalten — der Bericht geht ohne QR.`,
      );
      return null;
    }
    return ergebnis;
  } catch (e) {
    console.warn('Freimelde-Link konnte nicht vorbereitet werden — der Bericht geht ohne QR.', e);
    return null;
  }
};

/**
 * Der QR-Code eines BEREITS versendeten Berichts — rein lesend.
 *
 * Fuer Archiv-PDF und Vorschau: Wer einen alten Bericht noch einmal druckt,
 * bekommt denselben QR-Code wie die Empfaenger damals. Vor dem ersten Versand
 * gibt es keinen Link, also `null` — Vorschau und Download legen NIE einen an.
 *
 * Dasselbe Zeitbudget wie beim Versand: Vorschau und Archiv-Export duerfen
 * ebenso wenig an einer haengenden Abfrage kleben bleiben.
 *
 * DIE FREISCHALTUNG PRUEFT HIER NIEMAND — das tut der AUFRUFER. Diese
 * Funktion bekommt nur Kennungen (Projekt, Berichtsnummer, Abschnitt) und
 * kennt die Projekt-Metadaten nicht; sie nachzuladen waere eine zweite
 * Abfrage fuer eine Frage, die der Aufrufer schon beantworten kann. Beide
 * Aufrufer halten sich daran und fragen vorher `istFreimeldungAktiv`:
 * `PdfPreviewModal.ladeQr` (Vorschau) und der ZIP-Export in `HistoryView`
 * (Archiv). Wer einen dritten Aufrufer baut, muss dasselbe tun — sonst
 * druckt ein stillgelegtes Projekt weiter einen QR-Code, der ins Leere
 * fuehrt.
 */
export const ladeFreimeldungQrFuerBericht = async (
  projectId: string,
  inspectionNumber: number,
  sectionId: string | null | undefined,
  origin: string,
  pathname: string,
): Promise<FreimeldungQrQuelle | null> => {
  try {
    const id = (projectId || '').trim();
    const nummer = numOrNull(inspectionNumber);
    if (!id || !nummer) return null;

    const ergebnis = await mitZeitbudget(
      (async (): Promise<FreimeldungQrQuelle | null> => {
        const { data, error } = await supabase
          .from(T_LINKS)
          .select('token,aktiv_bis')
          .eq('project_id', id)
          .eq('inspection_number', nummer)
          .eq('section_key', sectionId ?? '')
          .maybeSingle();
        if (error) throw error;

        const row = (data ?? null) as Zeile | null;
        const token = row ? strOrNull(pick(row, 'token')) : null;
        if (!token) return null;
        if (linkAbgelaufen(pick(row as Zeile, 'aktivBis', 'aktiv_bis'))) return null;

        const url = freimeldungLinkUrl(origin, pathname, token);
        const dataUrl = await QRCode.toDataURL(url, { width: 512, margin: 1 });
        return { token, url, dataUrl, inspectionNumber: nummer };
      })(),
    );
    if (ergebnis === ZEIT_ABGELAUFEN) {
      console.warn(
        `Freimelde-QR nicht innerhalb von ${FREIMELDUNG_ZEITBUDGET_MS} ms geladen — der Export läuft ohne.`,
      );
      return null;
    }
    return ergebnis;
  } catch (e) {
    console.warn('Freimelde-QR konnte nicht geladen werden — der Export läuft ohne.', e);
    return null;
  }
};

// ─── Lesen ──────────────────────────────────────────────────────────────────

/**
 * Alle Meldungen eines Projekts, juengste zuerst. Fehlt die Tabelle (Migration
 * Nr. 108 offen), ist die Liste leer — die Oberflaeche zeigt dann schlicht
 * keine Freimeldungen statt einer Fehlermeldung.
 */
export const fetchFreimeldungen = async (projectId: string): Promise<ExterneFreimeldung[]> => {
  const id = (projectId || '').trim();
  if (!id) return [];
  const { data, error } = await supabase
    .from(T_MELDUNGEN)
    .select('*')
    .eq('project_id', id)
    .order('gemeldet_at', { ascending: false });
  if (error) {
    if (istFehlendesSchema(error)) return [];
    throw new Error(describeError(error));
  }
  return ((data ?? []) as Zeile[]).map(zeileZuFreimeldung);
};

/**
 * Nur die ZAHL der offenen Meldungen eines Projekts — fuer die Kachel im
 * ProjectHub. Der Hub braucht weder die Zeilen noch ihre Reihenfolge, nur den
 * Zaehler; eine reine Kopfzaehlung (`count: 'exact', head: true`) holt dafuer
 * keine einzige Zeile aus der Datenbank. `fetchFreimeldungen` + `zaehleOffene`
 * waeren hier die falsche Wahl: die Zeilenliste waechst mit jeder ENTSCHIEDENEN
 * Meldung mit (bestaetigt/abgelehnt bleiben liegen), die Kachel will aber nur
 * wissen, wie viele noch offen sind.
 *
 * Fehlt die Tabelle (Migration Nr. 108 offen), ist die Zahl 0 — dieselbe
 * Fail-open-Haltung wie bei `fetchFreimeldungen`.
 */
export const zaehleOffeneFreimeldungen = async (projectId: string): Promise<number> => {
  const id = (projectId || '').trim();
  if (!id) return 0;
  const { count, error } = await supabase
    .from(T_MELDUNGEN)
    .select('id', { count: 'exact', head: true })
    .eq('project_id', id)
    .eq('status', 'offen');
  if (error) {
    if (istFehlendesSchema(error)) return 0;
    throw new Error(describeError(error));
  }
  return count ?? 0;
};

/** Die Links eines Projekts (ein Link je versendetem Bericht), neueste zuerst. */
export const fetchFreimeldungLinks = async (projectId: string): Promise<FreimeldungLink[]> => {
  const id = (projectId || '').trim();
  if (!id) return [];
  const { data, error } = await supabase
    .from(T_LINKS)
    .select('*')
    .eq('project_id', id)
    .order('inspection_number', { ascending: false });
  if (error) {
    if (istFehlendesSchema(error)) return [];
    throw new Error(describeError(error));
  }
  return ((data ?? []) as Zeile[]).map(zeileZuLink);
};

/**
 * DER NACHWEIS: Was steht in der Datenbank wirklich an diesem Mangel?
 *
 * Rein lesend, genau eine Zeile. Die Frage ist der Ersatz fuer das frueher
 * benutzte Speichersignal: `saveStatus === 'saved'` gilt fuer den ganzen
 * Speicherlauf und nicht fuer ein einzelnes Projekt (useOfflineSync speichert
 * mit `flushAll` auch fremde Projekte mit), taugte also nie als Beleg dafuer,
 * dass GENAU DIESE Erledigung angekommen ist.
 *
 * WARUM NICHT MEHR NUR „geschlossen ja/nein": Eine Meldung wird nicht nur an
 * einem `closed` erledigt. Wird der Mangel nachtraeglich zur Information
 * (`general`) erklaert oder ganz geloescht, erreicht er `closed` NIE — die
 * Entscheidung stuende dann fuer immer in der Warteschlange, und genau dafuer
 * wurde sie frueher an der Schlange vorbei sofort gebucht (der Befund). Der
 * Status kommt deshalb unveraendert heraus, und `nachweisErlaubtBuchung` in
 * `freimeldungLogic.ts` entscheidet daraus.
 *
 * DIE FUENF ANTWORTEN:
 *   • `'closed'`  — Zeile da, Mangel erledigt (der Normalfall).
 *   • `'open'`    — Zeile da, Mangel steht weiter offen: NICHT buchen.
 *   • `'sonstig'` — Zeile da, aber weder offen noch erledigt (`general`,
 *     `info`, `good`): der Mangel ist als Aufgabe fort.
 *   • `'fehlt'`   — kein Fehler, keine Zeile: die Zeile gibt es in DIESEM
 *     Projekt nicht (geloescht, verschoben) — der Mangel ist fort.
 *   • `null`      — NICHT FESTSTELLBAR (Fehler, fehlende Tabelle, kein Netz,
 *     leere Argumente). Der Aufrufer laesst die Entscheidung in der
 *     Warteschlange und versucht es spaeter noch einmal.
 *
 * FAIL-CLOSED BLEIBT FAIL-CLOSED, anders als beim QR-Code: Alles, was nicht
 * gelesen werden konnte, ist `null` und damit kein Nachweis. Im schlimmsten
 * Fall bleibt die Meldung „offen" und der Koordinator entscheidet sie ein
 * zweites Mal. Der umgekehrte Fehler waere teuer: eine `bestaetigt` gebuchte
 * Meldung zu einem Mangel, der serverseitig offen steht, kommt nie wieder zum
 * Vorschein.
 *
 * `'fehlt'` UND RLS: Verweigert RLS den Lesezugriff, kommt keine Zeile und
 * KEIN Fehler — das sieht von hier aus wie eine geloeschte Zeile. Getragen
 * wird das, weil der Mangel dann ohnehin nicht mehr in der Ansicht des
 * Koordinators steht: er koennte ihn weder oeffnen noch erledigen, und die
 * Meldung bliebe sonst bis in alle Ewigkeit im Zaehler stehen.
 *
 * ZEITLICH BEGRENZT (`FREIMELDUNG_ZEITBUDGET_MS`): Eine Antwort, die NIE kommt,
 * ist schlimmer als ein Fehler. Der Aufrufer ist die Warteschlange im
 * SiGeKoInspectionModule; sie haelt waehrend des Laufs eine Sperre
 * (`freimeldungFlushLaeuft`), und jeder weitere Anlauf — Knopfdruck,
 * 30-Sekunden-Uhr — kehrt sofort um, solange die steht. Ohne Uhr blockierte
 * EINE haengende Lesefrage die ganze Schlange fuer die Sitzung: kein Versuch
 * wuerde gezaehlt, kein Hinweis erschiene, nichts wuerde je freigegeben.
 * Nach 8 Sekunden ist die Antwort `null` — nicht feststellbar, also bleibt die
 * Entscheidung in der Schlange und wird beim naechsten Lauf neu versucht.
 */
export const mangelServerStatus = async (
  projectId: string,
  itemId: string,
): Promise<MangelServerStatus | null> => {
  const pid = (projectId || '').trim();
  const iid = (itemId || '').trim();
  if (!pid || !iid) return null;
  try {
    const ergebnis = await mitZeitbudget(
      (async (): Promise<MangelServerStatus> => {
        const { data, error } = await supabase
          .from(T_ITEMS)
          .select('status')
          .eq('project_id', pid)
          .eq('id', iid)
          .maybeSingle();
        if (error) throw error;
        const row = (data ?? null) as Zeile | null;
        // KEIN FEHLER UND KEINE ZEILE heisst „gibt es hier nicht" — nicht
        // „unbekannt". Ein Fehler ist oben schon geworfen und landet im
        // `catch`.
        if (!row) return 'fehlt';
        const status = str(pick(row, 'status'));
        if (status === 'closed') return 'closed';
        if (status === 'open') return 'open';
        return 'sonstig';
      })(),
    );
    // Abgelaufene Zeit ist KEIN gelesener Stand, sondern dasselbe „nicht
    // feststellbar" wie ein Fehler — niemals `fehlt`, sonst wuerde auf eine
    // ausgebliebene Antwort hin gebucht.
    if (ergebnis === ZEIT_ABGELAUFEN) {
      console.warn(
        `Mangelstatus nicht innerhalb von ${FREIMELDUNG_ZEITBUDGET_MS} ms lesbar — die Entscheidung wartet weiter.`,
      );
      return null;
    }
    return ergebnis;
  } catch (e) {
    console.warn('Mangelstatus nicht lesbar — die Entscheidung wartet weiter.', e);
    return null;
  }
};

// ─── Entscheiden ────────────────────────────────────────────────────────────

export interface FreimeldungEntscheidung {
  ok: boolean;
  status: string;
  /** Weitere Meldungen zu DEMSELBEN Mangel, die mit erledigt wurden. */
  mitErledigt: number;
}

/**
 * Ein gescheiterter Entscheidungs-RPC MIT seinem SQLSTATE (Muster
 * `FreimeldungRpcFehler` auf der Melderseite).
 *
 * WARUM DER CODE MITMUSS: `describeError` macht aus dem Supabase-Fehlerobjekt
 * einen Satz fuer Menschen — und wirft damit weg, woran die Warteschlange im
 * SiGeKoInspectionModule das Einzige entscheidet, was sie entscheiden muss:
 * noch einmal versuchen oder aufgeben. Ein nacktes `new Error(text)` liess
 * jeden Fehler wie ein abgerissenes Netz aussehen, und ein „bereits anders
 * entschieden" (P0001) wurde alle 30 Sekunden bis zum Neuladen der Seite neu
 * versucht (`istEndgueltigerEntscheidungsfehler` in freimeldungLogic.ts).
 */
export class FreimeldungEntscheidungFehler extends Error {
  readonly code: string | null;
  constructor(message: string, code: string | null) {
    super(message);
    this.name = 'FreimeldungEntscheidungFehler';
    this.code = code;
  }
}

/**
 * Der SQLSTATE eines Fehlers, wenn er einen traegt — Supabase-Fehlerobjekt
 * ODER `FreimeldungEntscheidungFehler` (dieselbe Lesart wie
 * `freimeldungFehlerCode` auf der Melderseite). Ohne Code: `null`, und das
 * heisst „wiederholbar".
 */
export const entscheidungsFehlerCode = (e: unknown): string | null => {
  const code = (e as { code?: unknown } | null | undefined)?.code;
  if (typeof code !== 'string') return null;
  const c = code.trim();
  return c ? c : null;
};

/**
 * Bestaetigt oder lehnt eine Meldung ab (RPC `report_freimeldung_entscheiden`).
 *
 * WIRFT bei jedem Fehler — anders als der QR-Code ist das eine Handlung des
 * Koordinators, und sie darf nicht stumm verpuffen. Der Aufrufer zeigt den
 * Klartext, und zwar als `FreimeldungEntscheidungFehler`: der SQLSTATE haengt
 * am Fehler, nicht nur im Satz, sonst kann die Warteschlange einen Keil
 * (P0001, P0002, 22023, 42501) nicht von einem abgerissenen Netz
 * unterscheiden.
 *
 * `bestaetigt` erledigt zugleich alle anderen offenen Meldungen desselben
 * Mangels: Melden drei Firmen denselben Mangel frei, entscheidet der
 * Koordinator einmal und nicht dreimal.
 */
export const entscheideFreimeldung = async (
  id: string,
  entscheidung: 'bestaetigt' | 'abgelehnt',
  kommentar?: string,
): Promise<FreimeldungEntscheidung> => {
  const freimeldungId = (id || '').trim();
  // 22023 ist der SQLSTATE, den die RPC fuer eine abgelehnte Eingabe faende —
  // der Aufruf kaeme mit denselben Argumenten nie durch, also gar nicht erst
  // wiederholen.
  if (!freimeldungId) {
    throw new FreimeldungEntscheidungFehler('Ohne Freimeldung lässt sich nichts entscheiden.', '22023');
  }

  const { data, error } = await supabase.rpc('report_freimeldung_entscheiden', {
    p_id: freimeldungId,
    p_entscheidung: entscheidung,
    p_kommentar: (kommentar || '').trim() || null,
  });
  if (error) throw new FreimeldungEntscheidungFehler(describeError(error), entscheidungsFehlerCode(error));

  const row = alsObjekt(data) ?? {};
  return {
    ok: pick(row, 'ok') !== false,
    status: str(pick(row, 'status')) || entscheidung,
    mitErledigt: numOrNull(pick(row, 'mitErledigt', 'mit_erledigt')) ?? 0,
  };
};

/**
 * Dieselbe Entscheidung, nur gegen die Uhr (`FREIMELDUNG_ZEITBUDGET_MS`).
 *
 * WARUM NEBEN DER NACKTEN FASSUNG: Die Warteschlange im
 * SiGeKoInspectionModule haelt waehrend ihres Laufs eine Sperre; jeder weitere
 * Anlauf kehrt um, solange die steht. Ein RPC, der nie antwortet, liess die
 * Sperre fuer die Sitzung stehen — die Schlange kam nie wieder in Gang, kein
 * Versuch wurde gezaehlt, kein Hinweis erschien.
 *
 * WIRFT beim Ablauf, und zwar im Klartext: Der Aufrufer ist `entscheiden` im
 * Hook, und nur ein geworfener Fehler landet dort im `catch` — er setzt
 * `entscheidungsFehler`, meldet den Fehlschlag, und die Warteschlange behaelt
 * den Auftrag. Ein stilles `null` saehe von dort aus wie eine Buchung aus.
 *
 * OHNE SQLSTATE, UND ZWAR ABSICHTLICH: Eine Zeitueberschreitung ist KEIN
 * endgueltiger Fehler. Der Aufruf kann serverseitig laengst durchgelaufen sein
 * — wir haben die Antwort nur nicht mehr erlebt. Deshalb sagt der Satz hier
 * auch nicht mehr, die Entscheidung sei nicht gebucht (das war eine
 * Behauptung, die niemand pruefen konnte), sondern nur, dass es unklar ist —
 * OHNE ein Versprechen auf einen erneuten Versuch. Das ist nicht mehr Sache
 * dieser Funktion: Ueber die Warteschlange (`freimeldungWarteschlangeAbarbeiten`
 * im SiGeKoInspectionModule) laeuft der Auftrag tatsaechlich von selbst
 * erneut — gefahrlos, weil die RPC fuer DIESELBE Entscheidung idempotent ist,
 * sie entscheidet eine bereits entschiedene Meldung nicht ein zweites Mal —,
 * und GENAU DIESER Aufrufer haengt „Die App versucht es erneut." selbst an
 * seine Zusammenfassung. Der SOFORTIGE Pfad (`freimeldungEntscheidenOhneErgebnis`
 * fuer „Ablehnen") tut das nicht: dort fasst niemand automatisch nach, also
 * haengt er stattdessen „Bitte erneut versuchen." an. Ein Text, der hier schon
 * die Konsequenz nennt, waere fuer den einen Aufrufer richtig und fuer den
 * anderen falsch — deshalb bleibt er hier neutral.
 *
 * `ok: false` IST KEIN ERFOLG. Die RPC kann eine Antwort mit `ok: false`
 * liefern, ohne einen Fehler zu werfen (nichts getroffen, nichts gebucht).
 * Frueher lief die genauso durch wie eine Buchung: der Hook meldete Erfolg,
 * die Warteschlange strich den Auftrag, und die Entscheidung war fort. Ohne
 * SQLSTATE — der naechste Lauf darf es wieder versuchen.
 */
export const entscheideFreimeldungMitBudget = async (
  id: string,
  entscheidung: 'bestaetigt' | 'abgelehnt',
  kommentar?: string,
): Promise<FreimeldungEntscheidung> => {
  const ergebnis = await mitZeitbudget(entscheideFreimeldung(id, entscheidung, kommentar));
  if (ergebnis === ZEIT_ABGELAUFEN) {
    throw new Error('Zeitüberschreitung — ob die Entscheidung gebucht wurde, ist unklar.');
  }
  if (ergebnis.ok === false) throw new Error('Entscheidung wurde nicht gebucht.');
  return ergebnis;
};
